import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';

const CardPreview = ({ previewItem, cardImages, getImageUrl, onClose }) => {
  const [previewView, setPreviewView] = useState('cover'); // 'cover', 'inside1', 'inside2', 'back'

  // Reset to appropriate default view when previewItem changes
  useEffect(() => {
    if (!previewItem) {
      setPreviewView('cover');
      return;
    }
    
    // Determine default view: if no cover image but has text content, start with inside1
    const hasCoverImage = cardImages[previewItem.cardId] && (
      (typeof cardImages[previewItem.cardId] === 'string') ||
      cardImages[previewItem.cardId]?.cover
    );
    const hasTextContent = !!(previewItem.textContent?.inside1?.length || previewItem.textContent?.inside2?.length);
    
    if (!hasCoverImage && hasTextContent) {
      // If there's text content but no cover, start with inside1
      setPreviewView(previewItem.textContent?.inside1?.length ? 'inside1' : 'inside2');
    } else {
      setPreviewView('cover');
    }
  }, [previewItem, cardImages]);

  // Handle both old format (string) and new format (object with cover/inside1/inside2)
  const getImageId = (cardId, type = 'cover') => {
    if (!cardImages[cardId]) return null;
    if (typeof cardImages[cardId] === 'string') {
      // Old format: just cover image ID
      return type === 'cover' ? cardImages[cardId] : null;
    }
    // New format: object with cover/inside1/inside2
    return cardImages[cardId][type] || null;
  };

  // Allow preview even without images if there's text content or cardId
  if (!previewItem || !previewItem.cardId) {
    return null;
  }

  // Check if there's any content to show (images or text)
  const hasCoverImage = !!getImageId(previewItem.cardId, 'cover');
  const hasTextContent = !!(previewItem.textContent?.inside1?.length || previewItem.textContent?.inside2?.length);
  
  if (!hasCoverImage && !hasTextContent) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, p: 2 }}>
        <Typography color="text.secondary">No preview available</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
      {/* Thumbnail Navigation - Similar to personalize page */}
      <Box sx={{ 
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        gap: { xs: 0.5, sm: 1.5 },
        position: 'relative',
        minWidth: 0,
        flexWrap: 'wrap',
        pb: 2,
      }}>
        {[
          { id: 'cover', label: 'Cover' },
          { id: 'inside1', label: 'Inside Left' },
          { id: 'inside2', label: 'Inside Right' },
          { id: 'back', label: 'Back' }
        ].map((view) => {
          const isInside2 = view.id === 'inside2';
          return (
            <Box
              key={view.id}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                marginLeft: isInside2 ? { 
                  xs: 'calc(-0.5rem + 4px)', 
                  sm: 'calc(-1.5rem + 12px)' 
                } : 0,
              }}
            >
              <Box
                onClick={() => setPreviewView(view.id)}
                sx={{
                  width: { xs: 40, sm: 50 },
                  height: { xs: 56, sm: 70 },
                  aspectRatio: '5/7',
                  border: previewView === view.id ? '2px solid' : '1px solid',
                  borderColor: previewView === view.id ? 'primary.main' : 'grey.300',
                  borderRadius: 0.5,
                  overflow: 'hidden',
                  backgroundColor: 'white',
                  position: 'relative',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                  cursor: 'pointer',
                  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                  },
                }}
              >
                {view.id === 'cover' && getImageId(previewItem.cardId, 'cover') ? (
                  <img
                    src={getImageUrl(previewItem.cardId, getImageId(previewItem.cardId, 'cover'), 'preview')}
                    alt="Cover thumbnail"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                    onError={(e) => {
                      e.target.src = getImageUrl(previewItem.cardId, getImageId(previewItem.cardId, 'cover'), 'full');
                    }}
                  />
                ) : view.id === 'cover' ? (
                  // Cover view without image - show placeholder
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      backgroundColor: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                      Cover
                    </Typography>
                  </Box>
                ) : (view.id === 'inside1' || view.id === 'inside2') ? (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      width: '100%',
                      height: '100%',
                      backgroundImage: getImageId(previewItem.cardId, view.id) 
                        ? `url(${getImageUrl(previewItem.cardId, getImageId(previewItem.cardId, view.id), 'preview')})` 
                        : 'none',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      backgroundColor: getImageId(previewItem.cardId, view.id) ? 'transparent' : 'white',
                    }}
                  >
                    {/* Hidden img element to handle image loading errors */}
                    {getImageId(previewItem.cardId, view.id) && (
                      <img
                        src={getImageUrl(previewItem.cardId, getImageId(previewItem.cardId, view.id), 'preview')}
                        alt=""
                        style={{ display: 'none' }}
                        onError={(e) => {
                          // If the WebP preview fails, fall back to full size
                          e.target.src = getImageUrl(previewItem.cardId, getImageId(previewItem.cardId, view.id), 'full');
                        }}
                      />
                    )}
                    {/* Text overlay - use pre-rendered image if available */}
                    {previewItem.textContent?.[view.id]?.[0] && (() => {
                      const textElement = previewItem.textContent[view.id][0];
                      // If imageUrl exists, use the pre-rendered image
                      if (textElement.imageUrl) {
                        return (
                          <img
                            src={textElement.imageUrl}
                            alt="Card text"
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain',
                              zIndex: 1,
                            }}
                          />
                        );
                      }
                      // Fallback to rendering text if no imageUrl
                      if (textElement.text) {
                        // Scale factor for thumbnail: thumbnail is 50px wide, main preview is 566px wide
                        // Scale = 50/566 ≈ 0.088
                        const scale = 50 / 566;
                        // Scale padding proportionally (main preview uses padding: 48px)
                        const scaledPadding = 48 * scale;
                        // Calculate scaled font size and line height to match main preview proportionally
                        const baseFontSize = textElement.fontSize || 20;
                        const scaledFontSize = baseFontSize * scale;
                        // Use same line height ratio as main preview (1.4) for proportional scaling
                        const lineHeightValue = scaledFontSize * 1.4;
                        
                        return (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              padding: 0,
                              boxSizing: 'border-box',
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: `${scaledFontSize}px`,
                                fontFamily: `"${textElement.fontFamily}", ${textElement.fontCategory === 'serif' ? 'serif' : textElement.fontCategory === 'sans' ? 'sans-serif' : textElement.fontCategory === 'cursive' ? 'cursive' : textElement.fontCategory === 'handwritten' ? 'cursive' : textElement.fontCategory === 'script' ? 'cursive' : 'serif'}`,
                                color: textElement.color || '#495057',
                                fontWeight: textElement.fontWeight || 'normal',
                                fontStyle: textElement.fontStyle || 'normal',
                                textAlign: textElement.textAlign || 'left',
                                lineHeight: `${lineHeightValue}px`,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                width: '100%',
                                height: '100%',
                                padding: `${scaledPadding}px`,
                                boxSizing: 'border-box',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                textRendering: 'geometricPrecision',
                                WebkitFontSmoothing: 'antialiased',
                                MozOsxFontSmoothing: 'grayscale',
                              }}
                            >
                              {textElement.text || ''}
                            </Typography>
                          </Box>
                        );
                      }
                      return null;
                    })()}
                  </Box>
                ) : view.id === 'back' && previewItem.designerLogoUrl ? (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '40%',
                      height: '30%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <img
                      src={previewItem.designerLogoUrl}
                      alt="Designer Logo"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'contain',
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </Box>
                ) : null}
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Main Card Display - Show selected view only */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: 400,
        p: { xs: 1, sm: 2 },
      }}>
        <Box
          sx={{
            width: '100%',
            maxWidth: 566,
            aspectRatio: '5/7',
            border: '1px solid',
            borderColor: 'grey.300',
            borderRadius: 2,
            backgroundColor: 'white',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          }}
        >
          {/* Cover View */}
          {previewView === 'cover' && (
            <>
              {getImageId(previewItem.cardId, 'cover') ? (
                <img
                  src={getImageUrl(previewItem.cardId, getImageId(previewItem.cardId, 'cover'), 'full')}
                  alt="Card cover"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                  }}
                />
              ) : (
                // Cover without image - show placeholder or card info
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 4,
                    backgroundColor: 'white',
                  }}
                >
                  <Typography variant="h6" color="text.primary" sx={{ mb: 1 }}>
                    {previewItem.cardTitle || 'Card Cover'}
                  </Typography>
                  {previewItem.firstName || previewItem.name ? (
                    <Typography variant="body2" color="text.secondary">
                      For: {[previewItem.firstName, previewItem.lastName].filter(Boolean).join(' ') || previewItem.name || 'Recipient'}
                    </Typography>
                  ) : null}
                </Box>
              )}
            </>
          )}

          {/* Inside Left View */}
          {previewView === 'inside1' && (
            <>
              {/* Background image */}
              {getImageId(previewItem.cardId, 'inside1') && (
                <img
                  src={getImageUrl(previewItem.cardId, getImageId(previewItem.cardId, 'inside1'), 'full')}
                  alt="Inside left background"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    zIndex: 0,
                  }}
                />
              )}
              {/* Text overlay - use pre-rendered image if available */}
              {previewItem.textContent?.inside1 && previewItem.textContent.inside1.length > 0 && (
                previewItem.textContent.inside1.map((textElement) => {
                  // If imageUrl exists, use the pre-rendered image (matches what will be printed)
                  if (textElement.imageUrl) {
                    return (
                      <img
                        key={textElement.id}
                        src={textElement.imageUrl}
                        alt="Card text"
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          zIndex: 1,
                        }}
                      />
                    );
                  }
                  // Fallback to rendering text if no imageUrl (shouldn't happen for cart items)
                  return (
                    <Box
                      key={textElement.id}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        padding: 0,
                        boxSizing: 'border-box',
                        zIndex: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: `${textElement.fontSize}px`,
                          fontFamily: `"${textElement.fontFamily}", ${textElement.fontCategory === 'serif' ? 'serif' : textElement.fontCategory === 'sans' ? 'sans-serif' : textElement.fontCategory === 'cursive' ? 'cursive' : textElement.fontCategory === 'handwritten' ? 'cursive' : textElement.fontCategory === 'script' ? 'cursive' : 'serif'}`,
                          color: textElement.color || '#495057',
                          fontWeight: textElement.fontWeight || 'normal',
                          fontStyle: textElement.fontStyle || 'normal',
                          textAlign: textElement.textAlign || 'left',
                          lineHeight: 1.4,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          width: '100%',
                          height: '100%',
                          padding: '48px',
                          boxSizing: 'border-box',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          fontDisplay: 'swap',
                          textRendering: 'geometricPrecision',
                          WebkitFontSmoothing: 'antialiased',
                          MozOsxFontSmoothing: 'grayscale',
                          fontVariantLigatures: 'common-ligatures',
                          fontKerning: 'auto',
                          userSelect: 'none',
                          outline: 'none',
                        }}
                      >
                        {textElement.text || ''}
                      </Typography>
                    </Box>
                  );
                })
              )}
            </>
          )}

          {/* Inside Right View */}
          {previewView === 'inside2' && (
            <>
              {/* Background image */}
              {getImageId(previewItem.cardId, 'inside2') && (
                <img
                  src={getImageUrl(previewItem.cardId, getImageId(previewItem.cardId, 'inside2'), 'full')}
                  alt="Inside right background"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    zIndex: 0,
                  }}
                />
              )}
              {/* Text overlay - use pre-rendered image if available */}
              {previewItem.textContent?.inside2 && previewItem.textContent.inside2.length > 0 && (
                previewItem.textContent.inside2.map((textElement) => {
                  // If imageUrl exists, use the pre-rendered image (matches what will be printed)
                  if (textElement.imageUrl) {
                    return (
                      <img
                        key={textElement.id}
                        src={textElement.imageUrl}
                        alt="Card text"
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          zIndex: 1,
                        }}
                      />
                    );
                  }
                  // Fallback to rendering text if no imageUrl (shouldn't happen for cart items)
                  return (
                    <Box
                      key={textElement.id}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        padding: 0,
                        boxSizing: 'border-box',
                        zIndex: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: `${textElement.fontSize}px`,
                          fontFamily: `"${textElement.fontFamily}", ${textElement.fontCategory === 'serif' ? 'serif' : textElement.fontCategory === 'sans' ? 'sans-serif' : textElement.fontCategory === 'cursive' ? 'cursive' : textElement.fontCategory === 'handwritten' ? 'cursive' : textElement.fontCategory === 'script' ? 'cursive' : 'serif'}`,
                          color: textElement.color || '#495057',
                          fontWeight: textElement.fontWeight || 'normal',
                          fontStyle: textElement.fontStyle || 'normal',
                          textAlign: textElement.textAlign || 'left',
                          lineHeight: 1.4,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          width: '100%',
                          height: '100%',
                          padding: '48px',
                          boxSizing: 'border-box',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          fontDisplay: 'swap',
                          textRendering: 'geometricPrecision',
                          WebkitFontSmoothing: 'antialiased',
                          MozOsxFontSmoothing: 'grayscale',
                          fontVariantLigatures: 'common-ligatures',
                          fontKerning: 'auto',
                          userSelect: 'none',
                          outline: 'none',
                        }}
                      >
                        {textElement.text || ''}
                      </Typography>
                    </Box>
                  );
                })
              )}
            </>
          )}

          {/* Back View */}
          {previewView === 'back' && (
            <>
              {previewItem.designerLogoUrl && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    maxWidth: '40%',
                    maxHeight: '30%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={previewItem.designerLogoUrl}
                    alt="Designer Logo"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      width: 'auto',
                      height: 'auto',
                      objectFit: 'contain',
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default CardPreview;

